import { useEffect, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Gift, CalendarCheck, HandCoins, Disc3, Ticket, UsersRound, type LucideIcon } from 'lucide-react';
import { requireAuth } from '../features/auth/guards.js';
import { PageHeader } from '@/components/account/PageHeader.js';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs.js';
import { PromoOffersSection } from '@/components/promo/PromoOffersSection.js';
import { RedeemSection } from '@/components/promo/RedeemSection.js';
import { LuckySpinSection } from '@/components/spin/LuckySpinSection.js';
import { ReferralSection } from '@/components/referral/ReferralSection.js';
import { PROMO_GROUPS } from '@/features/promo/list-visibility.js';
import { SPIN_QUERY_KEYS, fetchSpinEvent, hasSpinWaiting } from '@/features/spin/api.js';

const BONUS_TABS = [...PROMO_GROUPS, 'spin', 'redeem', 'referral'] as const;
type BonusTab = (typeof BONUS_TABS)[number];

const TAB_ICONS: Record<BonusTab, LucideIcon> = {
  promotions: Gift,
  checkin: CalendarCheck,
  cashback: HandCoins,
  spin: Disc3,
  redeem: Ticket,
  referral: UsersRound,
};

export const Route = createFileRoute('/_player/promos/')({
  beforeLoad: requireAuth,
  // An unknown tab (a hand-edited or stale link) falls back to promotions
  // instead of failing the route.
  validateSearch: z.object({ tab: z.enum(BONUS_TABS).optional().catch(undefined) }),
  component: BonusHub,
});

function BonusHub() {
  const { t } = useTranslation('promo');
  const navigate = Route.useNavigate();
  const tab: BonusTab = Route.useSearch().tab ?? 'promotions';
  const tabListRef = useRef<HTMLDivElement>(null);
  const spinWaiting = hasSpinWaiting(
    useQuery({ queryKey: SPIN_QUERY_KEYS.event, queryFn: fetchSpinEvent, staleTime: 15_000 }).data,
  );

  // A deep link can land on a tab past the right edge of the strip on a phone.
  useEffect(() => {
    tabListRef.current
      ?.querySelector<HTMLElement>('[data-state="active"]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [tab]);

  const tabLabel: Record<BonusTab, string> = {
    promotions: t('hub.tabs.promotions'),
    checkin: t('hub.tabs.checkin'),
    cashback: t('hub.tabs.cashback'),
    spin: t('hub.tabs.spin'),
    redeem: t('hub.tabs.redeem'),
    referral: t('hub.tabs.referral'),
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('hub.title')}
        subtitle={t('hub.subtitle')}
        onBack={() => void navigate({ to: '/' })}
      />

      <Tabs
        value={tab}
        onValueChange={(next) => void navigate({ search: { tab: next as BonusTab }, replace: true })}
        className="flex flex-col gap-6"
      >
        <TabsList
          ref={tabListRef}
          aria-label={t('hub.tabsLabel')}
          className="flex w-auto justify-start gap-2 overflow-x-auto bf-noscroll rounded-none border-0 bg-transparent p-0 -mx-1 px-1 pb-1"
        >
          {BONUS_TABS.map((value) => {
            const TabIcon = TAB_ICONS[value];
            return (
              <TabsTrigger
                key={value}
                value={value}
                className="flex-none inline-flex items-center gap-2 h-10 px-4 rounded-xl text-[13.5px] font-bold bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)] data-[state=active]:bg-[var(--accent)] data-[state=active]:text-[var(--accent-on)] data-[state=active]:shadow-[0_6px_18px_var(--accent-glow)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
              >
                <TabIcon size={16} aria-hidden />
                {tabLabel[value]}
                {value === 'spin' && spinWaiting && (
                  <>
                    <span
                      aria-hidden
                      className="size-2 rounded-full bg-[var(--warning)] ring-2 ring-[var(--bg-raised)] motion-safe:animate-pulse"
                    />
                    <span className="sr-only">{t('hub.spinWaiting')}</span>
                  </>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {PROMO_GROUPS.map((group) => (
          <TabsContent key={group} value={group}>
            <PromoOffersSection group={group} />
          </TabsContent>
        ))}
        <TabsContent value="spin">
          <LuckySpinSection />
        </TabsContent>
        <TabsContent value="redeem">
          <RedeemSection />
        </TabsContent>
        <TabsContent value="referral">
          <ReferralSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
